import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { FloatingArrow, FloatingFocusManager, FloatingPortal, arrow, autoUpdate, flip, offset, safePolygon, shift, useClick, useDismiss, useFloating, useHover, useInteractions, useMergeRefs, useRole, useTransitionStyles, } from "@floating-ui/react";
import clsx from "clsx";
import { cloneElement, forwardRef, isValidElement, memo, useEffect, useRef, useState, } from "react";
import { Box } from "./Box";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx } from "../system";
export const popperClasses = {
    root: "MinervaPopper-root",
    "overflow-wrapper": "MinervaPopper-overflowWrapper",
    arrow: "MinervaPopper-arrow",
};
/**
 * ## Definition
 * The Popper component is a floating overlay that is used to display content on top of the current page.
 *
 * ## Behavior
 * - The open state of the popper is controlled by the `open` prop or can be used in an uncontrolled manner.
 * - The popper can be opened by clicking the trigger element (child) or by focussing on the node and pressing the `enter` or `space` keys.
 * - The popper will close when the user clicks outside of the popper, moves focus away from the trigger, or pressed the `enter` or `space` keys.
 */
export const Popper = memo(forwardRef((props, forwardedRef) => {
    const { content, placement, offset: _offset = 10, open: initOpen, removeFloatingArrow = false, disabled, configuration, onOpenChange, sx, className, children, ...rest } = useDefaultProps({ component: "Popper", props });
    const { openOnHover, preventCloseOnHoverBetweenTarget, preventCloseOnHoverAway, strategy } = configuration ?? {
        openOnHover: false,
        preventCloseOnHoverBetweenTarget: false,
        preventCloseOnHoverAway: false,
        strategy: "absolute",
    };
    const [open, setOpen] = useState(initOpen);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context } = useFloating({
        placement,
        open,
        strategy,
        onOpenChange: (open, _e, reason) => {
            if (preventCloseOnHoverAway &&
                !open &&
                (reason === "hover" || reason === "safe-polygon")) {
                return;
            }
            onOpenChange?.(open);
            setOpen(open);
        },
        middleware: [offset(_offset), flip(), shift(), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
    });
    const click = useClick(context, { enabled: !openOnHover && !disabled });
    const hover = useHover(context, {
        enabled: openOnHover && !disabled,
        handleClose: preventCloseOnHoverAway || preventCloseOnHoverBetweenTarget
            ? safePolygon()
            : undefined,
    });
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { styles: transitionStyles } = useTransitionStyles(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        hover,
        dismiss,
        role,
    ]);
    const mergedRefs = useMergeRefs([refs.setFloating, forwardedRef]);
    useEffect(() => {
        setOpen(initOpen);
    }, [initOpen]);
    return (_jsxs(_Fragment, { children: [children &&
                isValidElement(children) &&
                cloneElement(children, {
                    ref: refs.setReference,
                    "data-state": open ? "open" : "closed",
                    ...children.props,
                    ...getReferenceProps(),
                }), open && (_jsx(FloatingPortal, { children: _jsx(FloatingFocusManager, { context: context, modal: false, initialFocus: -1, children: _jsx(Box, { ref: mergedRefs, className: clsx(popperClasses.root, className), sx: mergeSx({
                            borderRadius: (theme) => theme.radius.default,
                            backgroundColor: (theme) => theme.palette.background.main,
                            color: (theme) => theme.palette.background.onMain,
                            boxShadow: (theme) => theme.shadows.md,
                            zIndex: (theme) => theme.zIndex.popper,
                            [`&.${popperClasses.root}`]: {
                                ...floatingStyles,
                                ...transitionStyles,
                            },
                            [`& .${popperClasses.arrow}`]: {
                                fill: (theme) => theme.palette.background.main,
                            },
                        }, sx), ...getFloatingProps(), ...rest, children: _jsxs(Box, { className: popperClasses["overflow-wrapper"], sx: {
                                overflow: "hidden",
                            }, children: [!removeFloatingArrow && (_jsx(FloatingArrow, { ref: arrowRef, context: context, className: popperClasses.arrow })), content] }) }) }) }))] }));
}));
//# sourceMappingURL=Popper.js.map